import React, { useMemo } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider } from '@mui/material/styles';
import { Typography } from '@mui/material';
import ErrorBoundary from '../../../common/components/errorBoundary';
import embedTheme from './theme';

/**
 * Provider shell around one mounted widget.
 *
 * Deliberately no `CssBaseline`: it emits `html`, `body` and `*` rules that
 * would restyle the customer's whole page. MUI's component styles are
 * emotion-scoped to generated class names, so leaving the baseline out keeps
 * everything this renders inside its own subtree.
 *
 * `container` decides where emotion injects. It is a prop rather than a
 * constant so that moving a mount into a shadow root later needs no change
 * here.
 */
const EmbedRoot = ({ container, attribution = null, children }) => {
    // `key` namespaces the generated class names, which also keeps them from
    // colliding with any MUI the host page runs itself.
    const cache = useMemo(
        () => createCache({
            key: 'omnigo',
            container: container?.ownerDocument?.head ?? document.head
        }),
        [container]
    );

    return (
        <CacheProvider value={cache}>
            <ThemeProvider theme={embedTheme}>
                <ErrorBoundary
                    fallback={
                        <Typography variant="body2" color="text.secondary">
                            Content unavailable.
                        </Typography>
                    }
                >
                    {children}
                </ErrorBoundary>
                {/* Outside the boundary: a widget that throws still gets attributed. */}
                {attribution}
            </ThemeProvider>
        </CacheProvider>
    );
};

export default EmbedRoot;
